import React, {
    useEffect,
    useRef,
    ReactNode,
    useState
} from 'react';

// Material ui
import { Grid } from '@material-ui/core';

// Components
import SelectInput, { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';
import AmountFormat from '@components/utils/AmountFormat';
import DateInput from '@components/UI-KIT/Inputs/DateInput';

// Types
import { TransactionDto } from '@services/account-transaction';
import { Property } from '@services/property';

// Icons
import {
    LeftArrow,
    Outgoing,
    Income,
    MovementOutlined
} from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';
import { Contract } from '@services/contract';

interface Props {
    transactionType: string;
    transaction: TransactionDto;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    setTransaction: React.Dispatch<React.SetStateAction<TransactionDto>>;
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: ReactNode | string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
        titleIcon: ReactNode;
    }>>;
    contractSelectOptions: SelectOption[];
    propertySelectOptions: SelectOption[];
    contractFilteredOptions: any;
    setContractFilteredOptions: any;
    propertyFilteredOptions: any;
    setPropertyFilteredOptions: any;
    selectedProperty: any;
    setSelectedProperty: any;
    selectedContract: any;
    setSelectedContract: any;
}

const IncludeStep: React.FC<Props> = ({
    transactionType,
    transaction,
    setActiveStep,
    setTransaction,
    setDrawer,
    contractSelectOptions,
    propertySelectOptions,
    contractFilteredOptions,
    setContractFilteredOptions,
    propertyFilteredOptions,
    setPropertyFilteredOptions,
    selectedProperty,
    setSelectedProperty,
    selectedContract,
    setSelectedContract
}) => {
    const [ { startDate, endDate }, setContractPeriods ] = useState({
        startDate: null,
        endDate: null
    });
    const associatedToOptions = useRef([
        {
            label: 'Inquilino',
            value: 'Tenant'
        },
        {
            label: 'Propietario',
            value: 'Propietor'
        },
        {
            label: 'Ambos',
            value: 'Both'
        }
    ]);

    useEffect(() => {
        setDrawer(prevState => ({
            ...prevState,
            drawerTitle: (
                <>
                    {`${transactionType === 'Income'
                        ? 'Ingreso de'
                        : transactionType === 'Outgoing'
                            ? 'Egreso de'
                            : 'Movimiento por'}
                         ARS`}
                    {'\xa0'}
                    <AmountFormat
                        value={transaction.amount}
                    />
                </>
            ),
            titleIcon: transactionType === 'Income'
                ? <Income />
                : transactionType === 'Outgoing'
                    ? <Outgoing />
                    : <MovementOutlined />
        }));
        const person = transaction.associatedTo.toLowerCase();
        const filteredContracts = contractSelectOptions.filter((option: SelectOption) => {
            if (person === 'propietor')
                return option.value.propietors[0].propietor.id === transaction.associatedPropietor;
            else return option.value.tenants[0].id === transaction.associatedTenant;
        });
        const filteredProperties = filteredContracts.map((option: SelectOption) => option.value.property.id);
        setContractFilteredOptions(filteredContracts);
        setPropertyFilteredOptions(propertySelectOptions.filter((option: SelectOption) => filteredProperties.includes(option.value.id)));
        if (transaction.property) setSelectedProperty(propertyFilteredOptions.find((option: SelectOption) => option.value.id === transaction.property).value);
        else if (transaction.associatedTo === 'Tenant') {
            const tenantProperty = propertySelectOptions.filter((option: SelectOption) => filteredProperties.includes(option.value.id))[0].value;
            const tenantContract = filteredContracts[0].value;
            setSelectedProperty(tenantProperty);
            setSelectedContract(tenantContract);
            setTransaction(prevState => ({
                ...prevState,
                property: tenantProperty.id,
                contract: tenantContract.id
            }));
        }
    }, [ ]);

    useEffect(() => {
        if (!selectedProperty) {
            setSelectedContract(null);
            setTransaction(prevState => ({
                ...prevState,
                contract: null
            }));
        } else {
            const propertyContract: Contract = contractFilteredOptions.find((option: SelectOption) => option.value.property.id === selectedProperty.id).value;
            setSelectedContract(propertyContract);
            setTransaction(prevState => ({
                ...prevState,
                contract: propertyContract.id
            }));
            setContractPeriods(() => {
                const handlePeriodDate = (date: string) => `${date.split('-')[0]}-${date.split('-')[1]}-01`;
                return {
                    endDate: handlePeriodDate(propertyContract.endDate),
                    startDate: handlePeriodDate(propertyContract.startDate)
                };
            });
        }

    }, [ selectedProperty ]);

    return (
        <>
            <SelectInput
                label="Propiedad"
                disabled={transaction.associatedTo === 'Tenant'}
                placeholder="Selecciona una opción.."
                value={propertyFilteredOptions.find((option: SelectOption) => option.value === selectedProperty)}
                onChange={(property: Property) => {
                    setSelectedProperty(property);
                    setTransaction(prevState => ({
                        ...prevState,
                        property: property?.id
                    }));
                }}
                options={propertyFilteredOptions}
            />
            <SelectInput
                disabled
                label="Contrato"
                placeholder="Selecciona una opción.."
                inputValue={selectedContract ? contractSelectOptions.find((option: SelectOption) => option.value === selectedContract).label : ' '}
                value={contractFilteredOptions.find((option: SelectOption) => option.value === selectedContract)}
                onChange={(contract: any) => {
                    setSelectedContract(contract);
                    setTransaction(prevState => ({
                        ...prevState,
                        contract: contract?.id
                    }));
                }}
                options={contractFilteredOptions}
            />
            <DateInput
                periodInput
                label="Período"
                minDate={startDate}
                maxDate={endDate}
                placeholder="Selecciona una opción.."
                value={transaction.period}
                onChange={(period: string) => setTransaction(prevState => ({
                    ...prevState,
                    period
                }))}
            />
            <SelectInput
                label="Imputación"
                value={associatedToOptions.current.find(({ value }: SelectOption) => value === transaction.imputation)}
                placeholder="Selecciona una opción.."
                onChange={(imputation: 'Tenant' | 'Propietor' | 'Both') => setTransaction(prevState => ({
                    ...prevState,
                    imputation
                }))}
                options={associatedToOptions.current}
            />
            <Footer
                className="AOL-Account-stepper-footer"
            >
                <Grid
                    container
                    justify="space-between"
                >
                    <Button
                        label="Anterior"
                        level={3}
                        icon={(
                            <LeftArrow
                                color={VARIABLES.palette.secondary_color}
                            />
                        )}
                        onClick={() => setActiveStep(prevState => prevState - 1)}
                    />
                    <Button
                        label="Siguiente"
                        disabled={!transaction.account || !transaction.concept || !transaction.associatedTo || !transaction.period}
                        onClick={() => setActiveStep(prevState => prevState + 1)}
                    />
                </Grid>
            </Footer>
        </>
    );
};

export default IncludeStep;